import React, { FC, useState, useEffect } from 'react';
import { Select, Input, Button, Row, Col, Typography, Space } from 'antd';
import { UserOutlined, PlusOutlined, DeleteOutlined, WarningOutlined, MailOutlined } from '@ant-design/icons';

import { FormContainer } from '../../../lib/components/Form/FormContainer';
import { FormField } from '../../../lib/components/Form/FormField';
import { FormButtons } from '../../../lib/components/Form/FormButtons';
import { AuthorReleaseFormProps, AuthorItem, ExtraPersonItem } from './types';

import { formatCPF, searchTagByCpf } from '../hooks/useAuthorReleaseApi';

const { Text } = Typography;

/**
 * Valida o CPF pelo cálculo dos dois dígitos verificadores.
 * Aceita CPF com ou sem pontuação.
 */
const isValidCPF = (cpf: string): boolean => {
	const digits = (cpf || '').replace(/\D/g, '');

	if (digits.length !== 11 || /^([0-9])\1{10}$/.test(digits)) {
		return false;
	}

	const calculateDigit = (base: string, factor: number): number => {
		const sum = base.split('').reduce((total, digit) => total + Number(digit) * factor--, 0);
		const remainder = (sum * 10) % 11;
		return remainder === 10 ? 0 : remainder;
	};

	const firstDigit = calculateDigit(digits.slice(0, 9), 10);
	if (firstDigit !== Number(digits[9])) {
		return false;
	}

	const secondDigit = calculateDigit(digits.slice(0, 10), 11);
	return secondDigit === Number(digits[10]);
};

const isValidEmail = (email?: string): boolean => !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const AuthorReleaseForm: FC<AuthorReleaseFormProps> = ({
	form_state,
	change_field_value,
	loading,
	disabled = false,
	on_submit,
	on_resend_author,
	publication_options,
	edition_options,
	on_change_content_type,
	on_change_publication,
	on_change_edition,
}) => {
	const { value } = form_state;

	const [searchValue, setSearchValue] = useState<string>('');
	const [resendingAuthorId, setResendingAuthorId] = useState<string | null>(null);

	/**
	 * Atualiza somente a mensagem de erro do CPF.
	 *
	 * IMPORTANTE:
	 * Esta função não altera o CPF.
	 */
	const handleAuthorError = (index: number, errorMessage?: string) => {
		const updatedAuthors = [...(value.authors || [])];

		if (updatedAuthors[index]) {
			updatedAuthors[index] = {
				...updatedAuthors[index],
				cpf_error: errorMessage,
			};

			change_field_value('authors', updatedAuthors);
		}
	};

	/**
	 * Valida se o CPF já está cadastrado.
	 *
	 * Regras:
	 *
	 * - CPF vazio:
	 *   é aceito, pois o campo é opcional.
	 *
	 * - CPF inválido:
	 *   mostra erro e não consulta a base.
	 *
	 * - CPF não encontrado / tag não encontrada:
	 *   mantém o CPF digitado.
	 *
	 * - CPF encontrado:
	 *   limpa o CPF e informa que já está cadastrado.
	 */
	const validateCpfAgainstAuthor = async (index: number, authorName: string, rawCpf: string) => {
		const cleanCpf = (rawCpf || '').replace(/\D/g, '');

		// CPF é opcional: se estiver vazio, não há erro.
		if (!cleanCpf) {
			handleAuthorError(index, undefined);
			return;
		}

		// Se foi informado, precisa ter 11 dígitos e ser matematicamente válido.
		if (!isValidCPF(cleanCpf)) {
			handleAuthorError(index, 'CPF inválido. Confira os números informados.');
			return;
		}

		try {
			const cpfData = await searchTagByCpf(cleanCpf);

			/**
			 * CPF NÃO ENCONTRADO
			 *
			 * searchTagByCpf retorna null quando não encontrou
			 * a tag correspondente.
			 *
			 * Nesse caso o CPF continua no campo.
			 */
			if (!cpfData) {
				handleAuthorError(index, undefined);
				return;
			}

			/**
			 * CPF JÁ CADASTRADO
			 *
			 * Limpa o CPF do campo e informa o usuário.
			 */
			const updatedAuthors = [...(value.authors || [])];

			if (updatedAuthors[index]) {
				updatedAuthors[index] = {
					...updatedAuthors[index],
					cpf: '',
					cpf_error: `CPF já cadastrado para "${cpfData.title}".`,
				};

				change_field_value('authors', updatedAuthors);
			}
		} catch (error) {
			/**
			 * Em caso de erro inesperado na consulta,
			 * não apagamos o CPF digitado pelo usuário.
			 */
			console.error('Erro ao validar CPF:', error);
		}
	};

	/**
	 * Altera os campos do autor.
	 *
	 * A validação do CPF NÃO é feita aqui.
	 *
	 * Isso permite que o usuário:
	 * - digite normalmente;
	 * - cole o CPF;
	 * - apague;
	 * - corrija o CPF.
	 */
	const handleAuthorChange = (index: number, field: keyof AuthorItem, val: string) => {
		const updatedAuthors = [...(value.authors || [])];

		const formattedVal = field === 'cpf' ? formatCPF(val) : val;

		updatedAuthors[index] = {
			...updatedAuthors[index],
			[field]: formattedVal,
		};

		/**
		 * Quando o usuário começa a alterar novamente
		 * o CPF, removemos o erro anterior.
		 */
		if (field === 'cpf') {
			updatedAuthors[index] = {
				...updatedAuthors[index],
				cpf_error: undefined,
			};
		}

		change_field_value('authors', updatedAuthors);
	};

	/**
	 * Executa a validação quando o usuário sai do campo CPF.
	 *
	 * Isso funciona tanto para CPF digitado quanto para CPF
	 * colado.
	 */
	const handleCpfBlur = async (index: number) => {
		const author = value.authors?.[index];

		if (!author) {
			return;
		}

		await validateCpfAgainstAuthor(index, author.name, author.cpf || '');
	};

	/**
	 * Valida CPFs que já chegam preenchidos ao selecionar
	 * uma edição.
	 */
	useEffect(() => {
		if (value.authors && value.authors.length > 0) {
			value.authors.forEach((author, index) => {
				if (author.cpf && author.cpf.replace(/\D/g, '').length === 11) {
					validateCpfAgainstAuthor(index, author.name, author.cpf);
				}
			});
		}
	}, [value.edition_id]);

	/**
	 * Reenvia o convite somente para o autor selecionado.
	 * O envio efetivo é delegado ao componente pai.
	 */
	const handleResendAuthor = async (author: AuthorItem) => {
		if (!isValidEmail(author.email) || disabled || loading || resendingAuthorId) {
			return;
		}

		setResendingAuthorId(author.id);
		try {
			await on_resend_author(author);
		} finally {
			setResendingAuthorId(null);
		}
	};

	/**
	 * Adiciona uma pessoa extra.
	 */
	const handleAddExtraPerson = () => {
		const newPerson: ExtraPersonItem = {
			id: Date.now().toString(),
			name: '',
			email: '',
		};

		change_field_value('extra_people', [...(value.extra_people || []), newPerson]);
	};

	/**
	 * Altera os dados de uma pessoa extra.
	 */
	const handleExtraPersonChange = (index: number, field: keyof ExtraPersonItem, val: string) => {
		const updatedPeople = [...(value.extra_people || [])];

		updatedPeople[index] = {
			...updatedPeople[index],
			[field]: val,
		};

		change_field_value('extra_people', updatedPeople);
	};

	/**
	 * Remove uma pessoa extra.
	 */
	const handleRemoveExtraPerson = (index: number) => {
		const updatedPeople = (value.extra_people || []).filter((_, i) => i !== index);

		change_field_value('extra_people', updatedPeople);
	};

	/**
	 * Verifica se existe algum erro de CPF.
	 *
	 * Se existir, o botão de cadastro permanece bloqueado.
	 */
	const hasCpfErrors = (value.authors || []).some((author) => !!author.cpf_error);

	// O e-mail continua obrigatório e deve ter um formato válido.
	const hasEmailErrors = (value.authors || []).some((author) => !isValidEmail(author.email));

	return (
		<FormContainer disabled={loading || disabled}>
			<FormField
				label='Selecione o tipo de conteúdo:'
				name='content_type'
				form_state={form_state}
				required
			>
				<Select
					disabled={disabled}
					value={value.content_type}
					onChange={(val) => {
						change_field_value('content_type', val);

						change_field_value('publication_id', undefined);

						change_field_value('edition_id', undefined);

						change_field_value('authors', []);

						setSearchValue('');

						if (val) {
							on_change_content_type?.(val, '');
						}
					}}
					options={[
						{
							label: 'Revista / Periódico',
							value: 'revista',
						},
						{
							label: 'Livro',
							value: 'livro',
						},
					]}
					placeholder='Selecione o tipo...'
					style={{
						width: '100%',
					}}
					allowClear
				/>
			</FormField>

			<FormField
				label={`Selecione a ${value.content_type === 'livro' ? 'Obra / Livro' : 'Revista'}`}
				name='publication_id'
				form_state={form_state}
				required
			>
				<Select
					showSearch
					value={value.publication_id}
					disabled={disabled || !value.content_type}
					options={publication_options}
					fieldNames={{
						label: 'label',
						value: 'value',
					}}
					optionFilterProp='label'
					placeholder={`Digite ou selecione a ${value.content_type === 'livro' ? 'obra' : 'revista'}...`}
					style={{
						width: '100%',
					}}
					onChange={(val) => {
						change_field_value('publication_id', val);

						change_field_value('edition_id', undefined);

						change_field_value('authors', []);

						if (val) {
							on_change_publication?.(val);
						}
					}}
					filterOption={(input, option) =>
						(option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
					}
				/>
			</FormField>

			<FormField label='Selecione a Edição:' name='edition_id' form_state={form_state} required>
				<Select
					value={value.edition_id}
					disabled={disabled || !value.publication_id}
					options={edition_options}
					onChange={(val) => {
						change_field_value('edition_id', val);

						on_change_edition?.(val);
					}}
					placeholder='Selecione a edição...'
					style={{
						width: '100%',
					}}
					showSearch
				/>
			</FormField>

			{value.edition_id && value.authors && value.authors.length > 0 && (
				<div
					style={{
						marginTop: 24,
						marginBottom: 24,
					}}
				>
					<Row
						gutter={12}
						style={{
							marginBottom: 8,
							fontWeight: 'bold',
						}}
					>
						<Col span={7}>
							<Text strong>Autores da Edição:</Text>
						</Col>

						<Col span={7}>
							<Text>CPF (Opcional):</Text>
						</Col>

						<Col span={10}>
							<Text type='danger'>Email (Obrigatório):</Text>
						</Col>
					</Row>

					{value.authors.map((author, index) => (
						<Row
							key={author.id || index}
							gutter={12}
							style={{
								marginBottom: 12,
							}}
							align='top'
						>
							<Col
								span={7}
								style={{
									paddingTop: 6,
								}}
							>
								<Space>
									<UserOutlined
										style={{
											color: '#1890ff',
										}}
									/>

									<Text strong>{author.name}</Text>
								</Space>
							</Col>

							<Col span={7}>
								<Input
									disabled={disabled}
									placeholder='000.000.000-00'
									maxLength={14}
									status={author.cpf_error ? 'error' : ''}
									value={author.cpf || ''}
									onChange={(e) => handleAuthorChange(index, 'cpf', e.target.value)}
									onBlur={() => handleCpfBlur(index)}
								/>

								{author.cpf_error && (
									<div
										style={{
											marginTop: 4,
										}}
									>
										<Text
											type='danger'
											style={{
												fontSize: 12,
											}}
										>
											{author.cpf_error}
										</Text>
									</div>
								)}
							</Col>

							<Col span={10}>
								<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
									<div style={{ flex: 1, minWidth: 0 }}>
										<Input
											disabled={disabled}
											placeholder='email@exemplo.com'
											status={!isValidEmail(author.email) ? 'error' : ''}
											value={author.email}
											onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
										/>
										{!isValidEmail(author.email) && (
											<div style={{ marginTop: 4 }}>
												<Text type='danger' style={{ fontSize: 12 }}>
													{author.email?.trim()
														? 'Informe um e-mail válido.'
														: 'E-mail obrigatório.'}
												</Text>
											</div>
										)}
									</div>
									<Button
										type='default'
										size='small'
										icon={<MailOutlined />}
										loading={resendingAuthorId === author.id}
										disabled={
											disabled || loading || !isValidEmail(author.email) || !!resendingAuthorId
										}
										onClick={() => handleResendAuthor(author)}
										style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
									>
										Reenviar
									</Button>
								</div>
							</Col>
						</Row>
					))}
				</div>
			)}

			<div
				style={{
					marginTop: 24,
					marginBottom: 24,
				}}
			>
				<Button
					type='default'
					icon={<PlusOutlined />}
					disabled={disabled}
					onClick={handleAddExtraPerson}
					style={{
						fontWeight: 'bold',
					}}
				>
					Adicionar mais pessoas
				</Button>

				{value.extra_people?.map((person, index) => (
					<Row
						key={person.id || index}
						gutter={12}
						style={{
							marginTop: 12,
						}}
						align='middle'
					>
						<Col span={11}>
							<Input
								disabled={disabled}
								placeholder='Informe o Nome'
								value={person.name}
								onChange={(e) => handleExtraPersonChange(index, 'name', e.target.value)}
							/>
						</Col>

						<Col span={11}>
							<Input
								disabled={disabled}
								placeholder='Informe o Email'
								value={person.email}
								onChange={(e) => handleExtraPersonChange(index, 'email', e.target.value)}
							/>
						</Col>

						<Col span={2}>
							<Button
								type='text'
								danger
								disabled={disabled}
								icon={<DeleteOutlined />}
								onClick={() => handleRemoveExtraPerson(index)}
							/>
						</Col>
					</Row>
				))}

				<div
					style={{
						marginTop: 12,
						display: 'flex',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<WarningOutlined
						style={{
							color: '#faad14',
							fontSize: 18,
						}}
					/>

					<Text type='secondary'>
						Não vamos salvar essas pessoas extras. Vamos apenas criar o perfil.
					</Text>
				</div>
			</div>

			<FormButtons
				disabled={disabled || loading || !form_state.dirty || hasCpfErrors || hasEmailErrors}
				go_back_on_cancel
				on_add_button_click={on_submit}
				on_update_button_click={undefined}
			/>
		</FormContainer>
	);
};

// import React, { FC, useState, useEffect } from 'react';
// import { Select, Input, Button, Row, Col, Typography, Space } from 'antd';
// import { UserOutlined, PlusOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';

// import { FormContainer } from '../../../lib/components/Form/FormContainer';
// import { FormField } from '../../../lib/components/Form/FormField';
// import { FormButtons } from '../../../lib/components/Form/FormButtons';
// import { AuthorReleaseFormProps, AuthorItem, ExtraPersonItem } from './types';

// import { formatCPF, searchTagByCpf } from '../hooks/useAuthorReleaseApi';

// const { Text } = Typography;

// const isValidCPF = (cpf: string): boolean => {
// 	const digits = (cpf || '').replace(/\D/g, '');

// 	if (digits.length !== 11 || /^([0-9])\1{10}$/.test(digits)) {
// 		return false;
// 	}

// 	const calculateDigit = (base: string, factor: number): number => {
// 		const sum = base.split('').reduce((total, digit) => total + Number(digit) * factor--, 0);
// 		const remainder = (sum * 10) % 11;
// 		return remainder === 10 ? 0 : remainder;
// 	};

// 	const firstDigit = calculateDigit(digits.slice(0, 9), 10);
// 	if (firstDigit !== Number(digits[9])) {
// 		return false;
// 	}

// 	const secondDigit = calculateDigit(digits.slice(0, 10), 11);
// 	return secondDigit === Number(digits[10]);
// };

// const isValidEmail = (email?: string): boolean => !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

// export const AuthorReleaseForm: FC<AuthorReleaseFormProps> = ({
// 	form_state,
// 	change_field_value,
// 	loading,
// 	disabled = false,
// 	on_submit,
// 	publication_options,
// 	edition_options,
// 	on_change_content_type,
// 	on_change_publication,
// 	on_change_edition,
// }) => {
// 	const { value } = form_state;

// 	const [searchValue, setSearchValue] = useState<string>('');

// 	/**
// 	 * Atualiza somente a mensagem de erro do CPF.
// 	 *
// 	 * IMPORTANTE:
// 	 * Esta função não altera o CPF.
// 	 */
// 	const handleAuthorError = (index: number, errorMessage?: string) => {
// 		const updatedAuthors = [...(value.authors || [])];

// 		if (updatedAuthors[index]) {
// 			updatedAuthors[index] = {
// 				...updatedAuthors[index],
// 				cpf_error: errorMessage,
// 			};

// 			change_field_value('authors', updatedAuthors);
// 		}
// 	};

// 	/**
// 	 * Valida se o CPF já está cadastrado.
// 	 *
// 	 * Regras:
// 	 *
// 	 * - CPF vazio:
// 	 *   é aceito, pois o campo é opcional.
// 	 *
// 	 * - CPF inválido:
// 	 *   mostra erro e não consulta a base.
// 	 *
// 	 * - CPF não encontrado / tag não encontrada:
// 	 *   mantém o CPF digitado.
// 	 *
// 	 * - CPF encontrado:
// 	 *   limpa o CPF e informa que já está cadastrado.
// 	 */
// 	const validateCpfAgainstAuthor = async (index: number, authorName: string, rawCpf: string) => {
// 		const cleanCpf = (rawCpf || '').replace(/\D/g, '');

// 		// CPF é opcional: se estiver vazio, não há erro.
// 		if (!cleanCpf) {
// 			handleAuthorError(index, undefined);
// 			return;
// 		}

// 		// Se foi informado, precisa ter 11 dígitos e ser matematicamente válido.
// 		if (!isValidCPF(cleanCpf)) {
// 			handleAuthorError(index, 'CPF inválido. Confira os números informados.');
// 			return;
// 		}

// 		try {
// 			const cpfData = await searchTagByCpf(cleanCpf);

// 			/**
// 			 * CPF NÃO ENCONTRADO
// 			 *
// 			 * searchTagByCpf retorna null quando não encontrou
// 			 * a tag correspondente.
// 			 *
// 			 * Nesse caso o CPF continua no campo.
// 			 */
// 			if (!cpfData) {
// 				handleAuthorError(index, undefined);
// 				return;
// 			}

// 			/**
// 			 * CPF JÁ CADASTRADO
// 			 *
// 			 * Limpa o CPF do campo e informa o usuário.
// 			 */
// 			const updatedAuthors = [...(value.authors || [])];

// 			if (updatedAuthors[index]) {
// 				updatedAuthors[index] = {
// 					...updatedAuthors[index],
// 					cpf: '',
// 					cpf_error: `CPF já cadastrado para "${cpfData.title}".`,
// 				};

// 				change_field_value('authors', updatedAuthors);
// 			}
// 		} catch (error) {
// 			/**
// 			 * Em caso de erro inesperado na consulta,
// 			 * não apagamos o CPF digitado pelo usuário.
// 			 */
// 			console.error('Erro ao validar CPF:', error);
// 		}
// 	};

// 	/**
// 	 * Altera os campos do autor.
// 	 *
// 	 * A validação do CPF NÃO é feita aqui.
// 	 *
// 	 * Isso permite que o usuário:
// 	 * - digite normalmente;
// 	 * - cole o CPF;
// 	 * - apague;
// 	 * - corrija o CPF.
// 	 */
// 	const handleAuthorChange = (index: number, field: keyof AuthorItem, val: string) => {
// 		const updatedAuthors = [...(value.authors || [])];

// 		const formattedVal = field === 'cpf' ? formatCPF(val) : val;

// 		updatedAuthors[index] = {
// 			...updatedAuthors[index],
// 			[field]: formattedVal,
// 		};

// 		/**
// 		 * Quando o usuário começa a alterar novamente
// 		 * o CPF, removemos o erro anterior.
// 		 */
// 		if (field === 'cpf') {
// 			updatedAuthors[index] = {
// 				...updatedAuthors[index],
// 				cpf_error: undefined,
// 			};
// 		}

// 		change_field_value('authors', updatedAuthors);
// 	};

// 	/**
// 	 * Executa a validação quando o usuário sai do campo CPF.
// 	 *
// 	 * Isso funciona tanto para CPF digitado quanto para CPF
// 	 * colado.
// 	 */
// 	const handleCpfBlur = async (index: number) => {
// 		const author = value.authors?.[index];

// 		if (!author) {
// 			return;
// 		}

// 		await validateCpfAgainstAuthor(index, author.name, author.cpf || '');
// 	};

// 	/**
// 	 * Valida CPFs que já chegam preenchidos ao selecionar
// 	 * uma edição.
// 	 */
// 	useEffect(() => {
// 		if (value.authors && value.authors.length > 0) {
// 			value.authors.forEach((author, index) => {
// 				if (author.cpf && author.cpf.replace(/\D/g, '').length === 11) {
// 					validateCpfAgainstAuthor(index, author.name, author.cpf);
// 				}
// 			});
// 		}
// 	}, [value.edition_id]);

// 	/**
// 	 * Adiciona uma pessoa extra.
// 	 */
// 	const handleAddExtraPerson = () => {
// 		const newPerson: ExtraPersonItem = {
// 			id: Date.now().toString(),
// 			name: '',
// 			email: '',
// 		};

// 		change_field_value('extra_people', [...(value.extra_people || []), newPerson]);
// 	};

// 	/**
// 	 * Altera os dados de uma pessoa extra.
// 	 */
// 	const handleExtraPersonChange = (index: number, field: keyof ExtraPersonItem, val: string) => {
// 		const updatedPeople = [...(value.extra_people || [])];

// 		updatedPeople[index] = {
// 			...updatedPeople[index],
// 			[field]: val,
// 		};

// 		change_field_value('extra_people', updatedPeople);
// 	};

// 	/**
// 	 * Remove uma pessoa extra.
// 	 */
// 	const handleRemoveExtraPerson = (index: number) => {
// 		const updatedPeople = (value.extra_people || []).filter((_, i) => i !== index);

// 		change_field_value('extra_people', updatedPeople);
// 	};

// 	/**
// 	 * Verifica se existe algum erro de CPF.
// 	 *
// 	 * Se existir, o botão de cadastro permanece bloqueado.
// 	 */
// 	const hasCpfErrors = (value.authors || []).some((author) => !!author.cpf_error);

// 	// O e-mail continua obrigatório e deve ter um formato válido.
// 	const hasEmailErrors = (value.authors || []).some((author) => !isValidEmail(author.email));

// 	return (
// 		<FormContainer disabled={loading || disabled}>
// 			<FormField
// 				label='Selecione o tipo de conteúdo:'
// 				name='content_type'
// 				form_state={form_state}
// 				required
// 			>
// 				<Select
// 					disabled={disabled}
// 					value={value.content_type}
// 					onChange={(val) => {
// 						change_field_value('content_type', val);

// 						change_field_value('publication_id', undefined);

// 						change_field_value('edition_id', undefined);

// 						change_field_value('authors', []);

// 						setSearchValue('');

// 						if (val) {
// 							on_change_content_type?.(val, '');
// 						}
// 					}}
// 					options={[
// 						{
// 							label: 'Revista / Periódico',
// 							value: 'revista',
// 						},
// 						{
// 							label: 'Livro',
// 							value: 'livro',
// 						},
// 					]}
// 					placeholder='Selecione o tipo...'
// 					style={{
// 						width: '100%',
// 					}}
// 					allowClear
// 				/>
// 			</FormField>

// 			<FormField
// 				label={`Selecione a ${value.content_type === 'livro' ? 'Obra / Livro' : 'Revista'}`}
// 				name='publication_id'
// 				form_state={form_state}
// 				required
// 			>
// 				<Select
// 					showSearch
// 					value={value.publication_id}
// 					disabled={disabled || !value.content_type}
// 					options={publication_options}
// 					fieldNames={{
// 						label: 'label',
// 						value: 'value',
// 					}}
// 					optionFilterProp='label'
// 					placeholder={`Digite ou selecione a ${value.content_type === 'livro' ? 'obra' : 'revista'}...`}
// 					style={{
// 						width: '100%',
// 					}}
// 					onChange={(val) => {
// 						change_field_value('publication_id', val);

// 						change_field_value('edition_id', undefined);

// 						change_field_value('authors', []);

// 						if (val) {
// 							on_change_publication?.(val);
// 						}
// 					}}
// 					filterOption={(input, option) =>
// 						(option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
// 					}
// 				/>
// 			</FormField>

// 			<FormField label='Selecione a Edição:' name='edition_id' form_state={form_state} required>
// 				<Select
// 					value={value.edition_id}
// 					disabled={disabled || !value.publication_id}
// 					options={edition_options}
// 					onChange={(val) => {
// 						change_field_value('edition_id', val);

// 						on_change_edition?.(val);
// 					}}
// 					placeholder='Selecione a edição...'
// 					style={{
// 						width: '100%',
// 					}}
// 					showSearch
// 				/>
// 			</FormField>

// 			{value.edition_id && value.authors && value.authors.length > 0 && (
// 				<div
// 					style={{
// 						marginTop: 24,
// 						marginBottom: 24,
// 					}}
// 				>
// 					<Row
// 						gutter={12}
// 						style={{
// 							marginBottom: 8,
// 							fontWeight: 'bold',
// 						}}
// 					>
// 						<Col span={8}>
// 							<Text strong>Autores da Edição:</Text>
// 						</Col>

// 						<Col span={8}>
// 							<Text>CPF (Opcional):</Text>
// 						</Col>

// 						<Col span={8}>
// 							<Text type='danger'>Email (Obrigatório):</Text>
// 						</Col>
// 					</Row>

// 					{value.authors.map((author, index) => (
// 						<Row
// 							key={author.id || index}
// 							gutter={12}
// 							style={{
// 								marginBottom: 12,
// 							}}
// 							align='top'
// 						>
// 							<Col
// 								span={8}
// 								style={{
// 									paddingTop: 6,
// 								}}
// 							>
// 								<Space>
// 									<UserOutlined
// 										style={{
// 											color: '#1890ff',
// 										}}
// 									/>

// 									<Text strong>{author.name}</Text>
// 								</Space>
// 							</Col>

// 							<Col span={8}>
// 								<Input
// 									disabled={disabled}
// 									placeholder='000.000.000-00'
// 									maxLength={14}
// 									status={author.cpf_error ? 'error' : ''}
// 									value={author.cpf || ''}
// 									onChange={(e) => handleAuthorChange(index, 'cpf', e.target.value)}
// 									onBlur={() => handleCpfBlur(index)}
// 								/>

// 								{author.cpf_error && (
// 									<div
// 										style={{
// 											marginTop: 4,
// 										}}
// 									>
// 										<Text
// 											type='danger'
// 											style={{
// 												fontSize: 12,
// 											}}
// 										>
// 											{author.cpf_error}
// 										</Text>
// 									</div>
// 								)}
// 							</Col>

// 							<Col span={8}>
// 								<Input
// 									disabled={disabled}
// 									placeholder='email@exemplo.com'
// 									status={!isValidEmail(author.email) ? 'error' : ''}
// 									value={author.email}
// 									onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
// 								/>
// 								{!isValidEmail(author.email) && (
// 									<div style={{ marginTop: 4 }}>
// 										<Text type='danger' style={{ fontSize: 12 }}>
// 											{author.email?.trim() ? 'Informe um e-mail válido.' : 'E-mail obrigatório.'}
// 										</Text>
// 									</div>
// 								)}
// 							</Col>
// 						</Row>
// 					))}
// 				</div>
// 			)}

// 			<div
// 				style={{
// 					marginTop: 24,
// 					marginBottom: 24,
// 				}}
// 			>
// 				<Button
// 					type='default'
// 					icon={<PlusOutlined />}
// 					disabled={disabled}
// 					onClick={handleAddExtraPerson}
// 					style={{
// 						fontWeight: 'bold',
// 					}}
// 				>
// 					Adicionar mais pessoas
// 				</Button>

// 				{value.extra_people?.map((person, index) => (
// 					<Row
// 						key={person.id || index}
// 						gutter={12}
// 						style={{
// 							marginTop: 12,
// 						}}
// 						align='middle'
// 					>
// 						<Col span={11}>
// 							<Input
// 								disabled={disabled}
// 								placeholder='Informe o Nome'
// 								value={person.name}
// 								onChange={(e) => handleExtraPersonChange(index, 'name', e.target.value)}
// 							/>
// 						</Col>

// 						<Col span={11}>
// 							<Input
// 								disabled={disabled}
// 								placeholder='Informe o Email'
// 								value={person.email}
// 								onChange={(e) => handleExtraPersonChange(index, 'email', e.target.value)}
// 							/>
// 						</Col>

// 						<Col span={2}>
// 							<Button
// 								type='text'
// 								danger
// 								disabled={disabled}
// 								icon={<DeleteOutlined />}
// 								onClick={() => handleRemoveExtraPerson(index)}
// 							/>
// 						</Col>
// 					</Row>
// 				))}

// 				<div
// 					style={{
// 						marginTop: 12,
// 						display: 'flex',
// 						alignItems: 'center',
// 						gap: 8,
// 					}}
// 				>
// 					<WarningOutlined
// 						style={{
// 							color: '#faad14',
// 							fontSize: 18,
// 						}}
// 					/>

// 					<Text type='secondary'>
// 						Não vamos salvar essas pessoas extras. Vamos apenas criar o perfil.
// 					</Text>
// 				</div>
// 			</div>

// 			<FormButtons
// 				disabled={disabled || loading || !form_state.dirty || hasCpfErrors || hasEmailErrors}
// 				go_back_on_cancel
// 				on_add_button_click={on_submit}
// 				on_update_button_click={undefined}
// 			/>
// 		</FormContainer>
// 	);
// };

// // import React, { FC, useState, useEffect } from 'react';
// // import { Select, Input, Button, Row, Col, Typography, Space } from 'antd';
// // import { UserOutlined, PlusOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';

// // import { FormContainer } from '../../../lib/components/Form/FormContainer';
// // import { FormField } from '../../../lib/components/Form/FormField';
// // import { FormButtons } from '../../../lib/components/Form/FormButtons';
// // import { AuthorReleaseFormProps, AuthorItem, ExtraPersonItem } from './types';

// // import { formatCPF, searchTagByCpf } from '../hooks/useAuthorReleaseApi';

// // const { Text } = Typography;

// // export const AuthorReleaseForm: FC<AuthorReleaseFormProps> = ({
// // 	form_state,
// // 	change_field_value,
// // 	loading,
// // 	disabled = false,
// // 	on_submit,
// // 	publication_options,
// // 	edition_options,
// // 	on_change_content_type,
// // 	on_change_publication,
// // 	on_change_edition,
// // }) => {
// // 	const { value } = form_state;

// // 	const [searchValue, setSearchValue] = useState<string>('');

// // 	/**
// // 	 * Atualiza somente a mensagem de erro do CPF.
// // 	 *
// // 	 * IMPORTANTE:
// // 	 * Esta função não altera o CPF.
// // 	 */
// // 	const handleAuthorError = (index: number, errorMessage?: string) => {
// // 		const updatedAuthors = [...(value.authors || [])];

// // 		if (updatedAuthors[index]) {
// // 			updatedAuthors[index] = {
// // 				...updatedAuthors[index],
// // 				cpf_error: errorMessage,
// // 			};

// // 			change_field_value('authors', updatedAuthors);
// // 		}
// // 	};

// // 	/**
// // 	 * Valida se o CPF já está cadastrado.
// // 	 *
// // 	 * Regras:
// // 	 *
// // 	 * - CPF incompleto:
// // 	 *   não valida.
// // 	 *
// // 	 * - CPF não encontrado / tag não encontrada:
// // 	 *   mantém o CPF digitado.
// // 	 *
// // 	 * - CPF encontrado:
// // 	 *   limpa o CPF e informa que já está cadastrado.
// // 	 */
// // 	const validateCpfAgainstAuthor = async (index: number, authorName: string, rawCpf: string) => {
// // 		const cleanCpf = (rawCpf || '').replace(/\D/g, '');

// // 		// CPF incompleto.
// // 		if (cleanCpf.length < 11) {
// // 			handleAuthorError(index, undefined);
// // 			return;
// // 		}

// // 		try {
// // 			const cpfData = await searchTagByCpf(cleanCpf);

// // 			/**
// // 			 * CPF NÃO ENCONTRADO
// // 			 *
// // 			 * searchTagByCpf retorna null quando não encontrou
// // 			 * a tag correspondente.
// // 			 *
// // 			 * Nesse caso o CPF continua no campo.
// // 			 */
// // 			if (!cpfData) {
// // 				handleAuthorError(index, undefined);
// // 				return;
// // 			}

// // 			/**
// // 			 * CPF JÁ CADASTRADO
// // 			 *
// // 			 * Limpa o CPF do campo e informa o usuário.
// // 			 */
// // 			const updatedAuthors = [...(value.authors || [])];

// // 			if (updatedAuthors[index]) {
// // 				updatedAuthors[index] = {
// // 					...updatedAuthors[index],
// // 					cpf: '',
// // 					cpf_error: `CPF já cadastrado para "${cpfData.title}".`,
// // 				};

// // 				change_field_value('authors', updatedAuthors);
// // 			}
// // 		} catch (error) {
// // 			/**
// // 			 * Em caso de erro inesperado na consulta,
// // 			 * não apagamos o CPF digitado pelo usuário.
// // 			 */
// // 			console.error('Erro ao validar CPF:', error);
// // 		}
// // 	};

// // 	/**
// // 	 * Altera os campos do autor.
// // 	 *
// // 	 * A validação do CPF NÃO é feita aqui.
// // 	 *
// // 	 * Isso permite que o usuário:
// // 	 * - digite normalmente;
// // 	 * - cole o CPF;
// // 	 * - apague;
// // 	 * - corrija o CPF.
// // 	 */
// // 	const handleAuthorChange = (index: number, field: keyof AuthorItem, val: string) => {
// // 		const updatedAuthors = [...(value.authors || [])];

// // 		const formattedVal = field === 'cpf' ? formatCPF(val) : val;

// // 		updatedAuthors[index] = {
// // 			...updatedAuthors[index],
// // 			[field]: formattedVal,
// // 		};

// // 		/**
// // 		 * Quando o usuário começa a alterar novamente
// // 		 * o CPF, removemos o erro anterior.
// // 		 */
// // 		if (field === 'cpf') {
// // 			updatedAuthors[index] = {
// // 				...updatedAuthors[index],
// // 				cpf_error: undefined,
// // 			};
// // 		}

// // 		change_field_value('authors', updatedAuthors);
// // 	};

// // 	/**
// // 	 * Executa a validação quando o usuário sai do campo CPF.
// // 	 *
// // 	 * Isso funciona tanto para CPF digitado quanto para CPF
// // 	 * colado.
// // 	 */
// // 	const handleCpfBlur = async (index: number) => {
// // 		const author = value.authors?.[index];

// // 		if (!author) {
// // 			return;
// // 		}

// // 		await validateCpfAgainstAuthor(index, author.name, author.cpf || '');
// // 	};

// // 	/**
// // 	 * Valida CPFs que já chegam preenchidos ao selecionar
// // 	 * uma edição.
// // 	 */
// // 	useEffect(() => {
// // 		if (value.authors && value.authors.length > 0) {
// // 			value.authors.forEach((author, index) => {
// // 				if (author.cpf && author.cpf.replace(/\D/g, '').length === 11) {
// // 					validateCpfAgainstAuthor(index, author.name, author.cpf);
// // 				}
// // 			});
// // 		}
// // 	}, [value.edition_id]);

// // 	/**
// // 	 * Adiciona uma pessoa extra.
// // 	 */
// // 	const handleAddExtraPerson = () => {
// // 		const newPerson: ExtraPersonItem = {
// // 			id: Date.now().toString(),
// // 			name: '',
// // 			email: '',
// // 		};

// // 		change_field_value('extra_people', [...(value.extra_people || []), newPerson]);
// // 	};

// // 	/**
// // 	 * Altera os dados de uma pessoa extra.
// // 	 */
// // 	const handleExtraPersonChange = (index: number, field: keyof ExtraPersonItem, val: string) => {
// // 		const updatedPeople = [...(value.extra_people || [])];

// // 		updatedPeople[index] = {
// // 			...updatedPeople[index],
// // 			[field]: val,
// // 		};

// // 		change_field_value('extra_people', updatedPeople);
// // 	};

// // 	/**
// // 	 * Remove uma pessoa extra.
// // 	 */
// // 	const handleRemoveExtraPerson = (index: number) => {
// // 		const updatedPeople = (value.extra_people || []).filter((_, i) => i !== index);

// // 		change_field_value('extra_people', updatedPeople);
// // 	};

// // 	/**
// // 	 * Verifica se existe algum erro de CPF.
// // 	 *
// // 	 * Se existir, o botão de cadastro permanece bloqueado.
// // 	 */
// // 	const hasCpfErrors = (value.authors || []).some((author) => !!author.cpf_error);

// // 	return (
// // 		<FormContainer disabled={loading || disabled}>
// // 			<FormField
// // 				label='Selecione o tipo de conteúdo:'
// // 				name='content_type'
// // 				form_state={form_state}
// // 				required
// // 			>
// // 				<Select
// // 					disabled={disabled}
// // 					value={value.content_type}
// // 					onChange={(val) => {
// // 						change_field_value('content_type', val);

// // 						change_field_value('publication_id', undefined);

// // 						change_field_value('edition_id', undefined);

// // 						change_field_value('authors', []);

// // 						setSearchValue('');

// // 						if (val) {
// // 							on_change_content_type?.(val, '');
// // 						}
// // 					}}
// // 					options={[
// // 						{
// // 							label: 'Revista / Periódico',
// // 							value: 'revista',
// // 						},
// // 						{
// // 							label: 'Livro',
// // 							value: 'livro',
// // 						},
// // 					]}
// // 					placeholder='Selecione o tipo...'
// // 					style={{
// // 						width: '100%',
// // 					}}
// // 					allowClear
// // 				/>
// // 			</FormField>

// // 			<FormField
// // 				label={`Selecione a ${value.content_type === 'livro' ? 'Obra / Livro' : 'Revista'}`}
// // 				name='publication_id'
// // 				form_state={form_state}
// // 				required
// // 			>
// // 				<Select
// // 					showSearch
// // 					value={value.publication_id}
// // 					disabled={disabled || !value.content_type}
// // 					options={publication_options}
// // 					fieldNames={{
// // 						label: 'label',
// // 						value: 'value',
// // 					}}
// // 					optionFilterProp='label'
// // 					placeholder={`Digite ou selecione a ${value.content_type === 'livro' ? 'obra' : 'revista'}...`}
// // 					style={{
// // 						width: '100%',
// // 					}}
// // 					onChange={(val) => {
// // 						change_field_value('publication_id', val);

// // 						change_field_value('edition_id', undefined);

// // 						change_field_value('authors', []);

// // 						if (val) {
// // 							on_change_publication?.(val);
// // 						}
// // 					}}
// // 					filterOption={(input, option) =>
// // 						(option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
// // 					}
// // 				/>
// // 			</FormField>

// // 			<FormField label='Selecione a Edição:' name='edition_id' form_state={form_state} required>
// // 				<Select
// // 					value={value.edition_id}
// // 					disabled={disabled || !value.publication_id}
// // 					options={edition_options}
// // 					onChange={(val) => {
// // 						change_field_value('edition_id', val);

// // 						on_change_edition?.(val);
// // 					}}
// // 					placeholder='Selecione a edição...'
// // 					style={{
// // 						width: '100%',
// // 					}}
// // 					showSearch
// // 				/>
// // 			</FormField>

// // 			{value.edition_id && value.authors && value.authors.length > 0 && (
// // 				<div
// // 					style={{
// // 						marginTop: 24,
// // 						marginBottom: 24,
// // 					}}
// // 				>
// // 					<Row
// // 						gutter={12}
// // 						style={{
// // 							marginBottom: 8,
// // 							fontWeight: 'bold',
// // 						}}
// // 					>
// // 						<Col span={8}>
// // 							<Text strong>Autores da Edição:</Text>
// // 						</Col>

// // 						<Col span={8}>
// // 							<Text type='danger'>CPF (Obrigatório):</Text>
// // 						</Col>

// // 						<Col span={8}>
// // 							<Text type='danger'>Email (Obrigatório):</Text>
// // 						</Col>
// // 					</Row>

// // 					{value.authors.map((author, index) => (
// // 						<Row
// // 							key={author.id || index}
// // 							gutter={12}
// // 							style={{
// // 								marginBottom: 12,
// // 							}}
// // 							align='top'
// // 						>
// // 							<Col
// // 								span={8}
// // 								style={{
// // 									paddingTop: 6,
// // 								}}
// // 							>
// // 								<Space>
// // 									<UserOutlined
// // 										style={{
// // 											color: '#1890ff',
// // 										}}
// // 									/>

// // 									<Text strong>{author.name}</Text>
// // 								</Space>
// // 							</Col>

// // 							<Col span={8}>
// // 								<Input
// // 									disabled={disabled}
// // 									placeholder='000.000.000-00'
// // 									maxLength={14}
// // 									status={author.cpf_error ? 'error' : ''}
// // 									value={author.cpf || ''}
// // 									onChange={(e) => handleAuthorChange(index, 'cpf', e.target.value)}
// // 									onBlur={() => handleCpfBlur(index)}
// // 								/>

// // 								{author.cpf_error && (
// // 									<div
// // 										style={{
// // 											marginTop: 4,
// // 										}}
// // 									>
// // 										<Text
// // 											type='danger'
// // 											style={{
// // 												fontSize: 12,
// // 											}}
// // 										>
// // 											{author.cpf_error}
// // 										</Text>
// // 									</div>
// // 								)}
// // 							</Col>

// // 							<Col span={8}>
// // 								<Input
// // 									disabled={disabled}
// // 									placeholder='email@exemplo.com'
// // 									value={author.email}
// // 									onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
// // 								/>
// // 							</Col>
// // 						</Row>
// // 					))}
// // 				</div>
// // 			)}

// // 			<div
// // 				style={{
// // 					marginTop: 24,
// // 					marginBottom: 24,
// // 				}}
// // 			>
// // 				<Button
// // 					type='default'
// // 					icon={<PlusOutlined />}
// // 					disabled={disabled}
// // 					onClick={handleAddExtraPerson}
// // 					style={{
// // 						fontWeight: 'bold',
// // 					}}
// // 				>
// // 					Adicionar mais pessoas
// // 				</Button>

// // 				{value.extra_people?.map((person, index) => (
// // 					<Row
// // 						key={person.id || index}
// // 						gutter={12}
// // 						style={{
// // 							marginTop: 12,
// // 						}}
// // 						align='middle'
// // 					>
// // 						<Col span={11}>
// // 							<Input
// // 								disabled={disabled}
// // 								placeholder='Informe o Nome'
// // 								value={person.name}
// // 								onChange={(e) => handleExtraPersonChange(index, 'name', e.target.value)}
// // 							/>
// // 						</Col>

// // 						<Col span={11}>
// // 							<Input
// // 								disabled={disabled}
// // 								placeholder='Informe o Email'
// // 								value={person.email}
// // 								onChange={(e) => handleExtraPersonChange(index, 'email', e.target.value)}
// // 							/>
// // 						</Col>

// // 						<Col span={2}>
// // 							<Button
// // 								type='text'
// // 								danger
// // 								disabled={disabled}
// // 								icon={<DeleteOutlined />}
// // 								onClick={() => handleRemoveExtraPerson(index)}
// // 							/>
// // 						</Col>
// // 					</Row>
// // 				))}

// // 				<div
// // 					style={{
// // 						marginTop: 12,
// // 						display: 'flex',
// // 						alignItems: 'center',
// // 						gap: 8,
// // 					}}
// // 				>
// // 					<WarningOutlined
// // 						style={{
// // 							color: '#faad14',
// // 							fontSize: 18,
// // 						}}
// // 					/>

// // 					<Text type='secondary'>
// // 						Não vamos salvar essas pessoas extras. Vamos apenas criar o perfil.
// // 					</Text>
// // 				</div>
// // 			</div>

// // 			<FormButtons
// // 				disabled={disabled || loading || !form_state.dirty || hasCpfErrors}
// // 				go_back_on_cancel
// // 				on_add_button_click={on_submit}
// // 				on_update_button_click={undefined}
// // 			/>
// // 		</FormContainer>
// // 	);
// // };
